import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FileText, Plus, Search, Pencil, Trash2, Camera, CreditCard, Clock, CheckCircle2, AlertTriangle, Filter, X, Receipt, QrCode, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { useTranslation } from 'react-i18next';

const TVA_RATE = 8.1;

const STATUSES = [
  { value: 'a_payer', label: 'A payer', color: 'bg-amber-100 text-amber-700' },
  { value: 'payee', label: 'Payee', color: 'bg-green-100 text-green-700' },
  { value: 'en_retard', label: 'En retard', color: 'bg-red-100 text-red-700' },
  { value: 'contestee', label: 'Contestee', color: 'bg-slate-100 text-slate-600' },
];

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'pieces', label: 'Pieces detachees' },
  { value: 'pneus', label: 'Pneus' },
  { value: 'outillage', label: 'Outillage' },
  { value: 'services', label: 'Services' },
  { value: 'autre', label: 'Autre' },
];

const emptyLine = { description: '', quantity: 1, unit_price: 0, tva_rate: TVA_RATE };

const StatusBadge = ({ status }) => {
  const s = STATUSES.find(x => x.value === status) || { label: status, color: 'bg-slate-100 text-slate-600' };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`} data-testid={`status-${status}`}>{s.label}</span>;
};

const CategoryBadge = ({ category }) => {
  const c = CATEGORIES.find(x => x.value === category);
  return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">{c?.label || category}</span>;
};

const SupplierInvoicesPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState('list');
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [tvaMonthly, setTvaMonthly] = useState(null);
  const [tvaYear, setTvaYear] = useState(new Date().getFullYear());
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({
    supplier_name: '', invoice_number: '', reference: '', invoice_date: '', due_date: '',
    lines: [{ ...emptyLine }], status: 'a_payer', payment_method: '', category: 'general', notes: '',
    payment_terms: '',
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [fromScan, setFromScan] = useState(false);
  const fileInputRef = useRef(null);

  // QR Code scanning state
  const [showQrDialog, setShowQrDialog] = useState(false);
  const [scanSession, setScanSession] = useState(null);
  const [pollingSession, setPollingSession] = useState(false);
  const pollRef = useRef(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, statsRes] = await Promise.all([
        axios.get(`${API}/supplier-invoices`),
        axios.get(`${API}/supplier-invoices/stats`),
      ]);
      setItems(itemsRes.data);
      setStats(statsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  const loadTvaMonthly = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/supplier-invoices/tva-monthly`, { params: { year: tvaYear } });
      setTvaMonthly(res.data);
    } catch (err) { console.error(err); }
  }, [tvaYear]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { if (tab === 'tva') loadTvaMonthly(); }, [tab, loadTvaMonthly]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const openAdd = () => {
    setEditItem(null);
    setFromScan(false);
    setForm({
      supplier_name: '', invoice_number: '', reference: '', invoice_date: '', due_date: '',
      lines: [{ ...emptyLine }], status: 'a_payer', payment_method: '', category: 'general', notes: '',
      payment_terms: '',
    });
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setFromScan(false);
    setForm({
      supplier_name: item.supplier_name || '',
      invoice_number: item.invoice_number || '',
      reference: item.reference || '',
      invoice_date: item.invoice_date || '',
      due_date: item.due_date || '',
      lines: item.lines?.length ? item.lines.map(l => ({ ...l })) : [{ ...emptyLine }],
      status: item.status || 'a_payer',
      payment_method: item.payment_method || '',
      category: item.category || 'general',
      notes: item.notes || '',
    });
    setShowForm(true);
  };

  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, { ...emptyLine }] }));

  const applyPaymentTerms = (terms, invoiceDate) => {
    if (!terms || !invoiceDate) return '';
    const days = parseInt(terms);
    if (isNaN(days)) return '';
    const d = new Date(invoiceDate);
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  };
  const removeLine = (idx) => setForm(f => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));
  const updateLine = (idx, field, value) => {
    setForm(f => {
      const lines = [...f.lines];
      lines[idx] = { ...lines[idx], [field]: value };
      return { ...f, lines };
    });
  };

  const calcTotal = (lines) => {
    let sub = 0, tva = 0;
    for (const l of lines) {
      const ht = (l.quantity || 0) * (l.unit_price || 0);
      sub += ht;
      tva += ht * (l.tva_rate || TVA_RATE) / 100;
    }
    return { subtotal: sub.toFixed(2), tva: tva.toFixed(2), total: (sub + tva).toFixed(2) };
  };

  const handleSave = async () => {
    if (!form.supplier_name) { toast.error('Nom du fournisseur requis'); return; }
    if (!form.lines.length || !form.lines[0].description) { toast.error('Ajoutez au moins une ligne'); return; }
    try {
      if (editItem) {
        await axios.put(`${API}/supplier-invoices/${editItem.id}`, form);
        toast.success('Facture fournisseur mise a jour');
      } else {
        await axios.post(`${API}/supplier-invoices`, form);
        toast.success('Facture fournisseur creee');
      }
      setShowForm(false);
      loadData();
      if (tab === 'tva') loadTvaMonthly();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await axios.delete(`${API}/supplier-invoices/${deleteConfirm.id}`);
      toast.success('Facture supprimee');
      setDeleteConfirm(null);
      loadData();
      if (tab === 'tva') loadTvaMonthly();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleStatusChange = async (item, newStatus) => {
    try {
      const data = { status: newStatus };
      if (newStatus === 'payee') data.payment_date = new Date().toISOString().slice(0, 10);
      await axios.put(`${API}/supplier-invoices/${item.id}`, data);
      toast.success('Statut mis a jour');
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  // Direct file scan (desktop)
  const handleDirectScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/supplier-invoices/scan`, formData);
      const data = res.data;
      setForm({
        supplier_name: data.supplier_name || '',
        invoice_number: data.invoice_number || '',
        reference: data.reference || '',
        invoice_date: data.invoice_date || '',
        due_date: data.due_date || '',
        lines: data.lines?.length ? data.lines : [{ ...emptyLine }],
        status: 'a_payer',
        payment_method: '',
        category: data.category || 'general',
        notes: '',
        payment_terms: '',
      });
      setEditItem(null);
      setFromScan(true);
      setShowForm(true);
      toast.success('Facture scannee !');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de scan');
    } finally {
      setScanning(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // QR Code scan workflow (mobile phone)
  const startQrScan = async () => {
    try {
      const res = await axios.post(`${API}/supplier-invoices/scan-sessions`);
      setScanSession(res.data);
      setShowQrDialog(true);
      setPollingSession(true);

      // Start polling for session result
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const sRes = await axios.get(`${API}/supplier-invoices/scan-sessions/${res.data.id}`);
          const sess = sRes.data;
          if (sess.status === 'completed' && sess.result) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setPollingSession(false);
            setShowQrDialog(false);
            const data = sess.result;
            setForm({
              supplier_name: data.supplier_name || '',
              invoice_number: data.invoice_number || '',
              reference: data.reference || '',
              invoice_date: data.invoice_date || '',
              due_date: data.due_date || '',
              lines: data.lines?.length ? data.lines : [{ ...emptyLine }],
              status: 'a_payer',
              payment_method: '',
              category: data.category || 'general',
              notes: '',
              payment_terms: '',
            });
            setEditItem(null);
            setFromScan(true);
            setShowForm(true);
            toast.success('Facture scannee depuis le telephone !');
          } else if (sess.status === 'error') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setPollingSession(false);
            toast.error(sess.error || 'Erreur de scan');
          }
        } catch { /* keep polling */ }
      }, 2000);
    } catch (err) {
      toast.error('Erreur de creation de session');
    }
  };

  const closeQrDialog = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setShowQrDialog(false);
    setPollingSession(false);
    setScanSession(null);
  };

  const filtered = items.filter(i => {
    if (filterStatus !== 'all' && i.status !== filterStatus) return false;
    if (filterCategory !== 'all' && i.category !== filterCategory) return false;
    if (search) {
      const s = search.toLowerCase();
      return i.supplier_name?.toLowerCase().includes(s) ||
        i.invoice_number?.toLowerCase().includes(s) ||
        i.notes?.toLowerCase().includes(s);
    }
    return true;
  });

  const totals = calcTotal(form.lines);

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-4 md:p-6 space-y-6" data-testid="supplier-invoices-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="supplier-invoices-title">Factures Fournisseurs</h1>
            <p className="text-sm text-slate-500">Gestion des factures a payer et suivi TVA</p>
          </div>
          <div className="flex gap-2">
            <input type="file" ref={fileInputRef} accept="image/*" onChange={handleDirectScan} className="hidden" data-testid="scan-input" />
            <Button onClick={() => fileInputRef.current?.click()} variant="outline" disabled={scanning} data-testid="scan-file-btn" size="sm">
              {scanning ? <Clock className="w-4 h-4 mr-1 animate-spin" /> : <Camera className="w-4 h-4 mr-1" />}
              {scanning ? 'Analyse...' : 'Fichier'}
            </Button>
            <Button onClick={startQrScan} variant="outline" data-testid="scan-qr-btn" size="sm">
              <QrCode className="w-4 h-4 mr-1" /> Téléphone
            </Button>
            <Button onClick={openAdd} className="bg-blue-600 hover:bg-blue-700" data-testid="add-supplier-invoice-btn" size="sm">
              <Plus className="w-4 h-4 mr-1" /> Ajouter
            </Button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="p-4" data-testid="stat-total">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 rounded-lg"><FileText className="w-5 h-5 text-blue-600" /></div>
                <div>
                  <p className="text-xs text-slate-500">Total factures</p>
                  <p className="text-lg font-bold">{stats.total_count}</p>
                </div>
              </div>
            </Card>
            <Card className="p-4" data-testid="stat-a-payer">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-50 rounded-lg"><Clock className="w-5 h-5 text-amber-600" /></div>
                <div>
                  <p className="text-xs text-slate-500">A payer</p>
                  <p className="text-lg font-bold text-amber-700">CHF {stats.total_a_payer?.toFixed(2)}</p>
                  <p className="text-xs text-slate-400">{stats.a_payer_count + stats.en_retard_count} facture(s)</p>
                </div>
              </div>
            </Card>
            <Card className="p-4" data-testid="stat-paye">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-50 rounded-lg"><CheckCircle2 className="w-5 h-5 text-green-600" /></div>
                <div>
                  <p className="text-xs text-slate-500">{t('billing.paid')}</p>
                  <p className="text-lg font-bold text-green-700">CHF {stats.total_paye?.toFixed(2)}</p>
                  <p className="text-xs text-slate-400">{stats.payee_count} facture(s)</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 border-2 border-red-200" data-testid="stat-tva">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-50 rounded-lg"><Receipt className="w-5 h-5 text-red-600" /></div>
                <div>
                  <p className="text-xs text-slate-500 font-semibold">TVA TOTALE</p>
                  <p className="text-lg font-bold text-red-700">CHF {stats.total_tva?.toFixed(2)}</p>
                  {stats.tva_summary?.map(t => (
                    <p key={t.rate} className="text-xs text-slate-500">{t.rate}%: CHF {t.amount?.toFixed(2)}</p>
                  ))}
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Tabs: List / TVA */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="supplier-tabs">
            <TabsTrigger value="list" data-testid="tab-list">Factures ({items.length})</TabsTrigger>
            <TabsTrigger value="tva" data-testid="tab-tva">TVA a recuperer</TabsTrigger>
          </TabsList>

          {/* ─── FACTURES TAB ─── */}
          <TabsContent value="list" className="mt-4 space-y-4">
            {/* Search + Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Rechercher fournisseur, numéro..." value={search} onChange={e => setSearch(e.target.value)}
                  className="pl-10" data-testid="supplier-search" />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-40" data-testid="filter-status">
                  <Filter className="w-3 h-3 mr-1" />
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-40" data-testid="filter-category">
                  <SelectValue placeholder="Categorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes categories</SelectItem>
                  {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* List */}
            {loading ? <p className="text-slate-500 text-center py-8">{t('common.loading')}</p> :
              filtered.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Aucune facture fournisseur</p>
                  <p className="text-sm text-slate-400 mt-1">Scannez ou ajoutez manuellement une facture</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filtered.map(inv => (
                    <Card key={inv.id} className="p-4 hover:shadow-md transition-shadow" data-testid={`supplier-inv-${inv.id}`}>
                      <div className="flex flex-col sm:flex-row justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="font-bold text-slate-800">{inv.supplier_name}</span>
                            {inv.invoice_number && <span className="font-mono text-sm text-blue-600">{inv.invoice_number}</span>}
                            {inv.reference && <span className="font-mono text-xs text-slate-400">Ref: {inv.reference}</span>}
                            <StatusBadge status={inv.status} />
                            <CategoryBadge category={inv.category} />
                          </div>
                          <div className="flex gap-4 text-xs text-slate-500 mt-1">
                            {inv.invoice_date && <span>Date: {inv.invoice_date}</span>}
                            {inv.due_date && <span>Échéance : {inv.due_date}</span>}
                            <span>{inv.lines?.length || 0} ligne(s)</span>
                          </div>
                          {inv.tva_breakdown?.length > 0 && (
                            <div className="flex gap-2 mt-1">
                              {inv.tva_breakdown.map((t, idx) => (
                                <span key={idx} className="text-xs bg-red-50 text-red-600 px-1.5 py-0.5 rounded">
                                  TVA {t.rate}%: CHF {t.amount?.toFixed(2)}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <div className="text-right">
                            <p className="text-xs text-slate-500">HT: CHF {inv.subtotal_ht?.toFixed(2)}</p>
                            <p className="text-xs text-red-600 font-medium">TVA: CHF {inv.tva_total?.toFixed(2)}</p>
                            <p className="text-lg font-bold text-slate-900">CHF {inv.total_ttc?.toFixed(2)}</p>
                          </div>
                          <div className="flex gap-1 flex-wrap justify-end">
                            <Button size="sm" variant="ghost" onClick={() => openEdit(inv)} data-testid={`edit-${inv.id}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            {inv.status !== 'payee' && (
                              <Button size="sm" variant="ghost" className="text-green-600" onClick={() => handleStatusChange(inv, 'payee')} data-testid={`pay-${inv.id}`}>
                                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Payer
                              </Button>
                            )}
                            {inv.status !== 'payee' && (
                              <Button size="sm" variant="ghost" className="text-red-500" onClick={() => setDeleteConfirm(inv)} data-testid={`delete-${inv.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                          {inv.status !== 'payee' && (
                            <Select value={inv.status} onValueChange={(v) => handleStatusChange(inv, v)}>
                              <SelectTrigger className="w-32 h-7 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
          </TabsContent>

          {/* ─── TVA TAB ─── */}
          <TabsContent value="tva" className="mt-4 space-y-4">
            {/* Year navigation */}
            <div className="flex items-center justify-center gap-4">
              <Button variant="outline" size="sm" onClick={() => setTvaYear(y => y - 1)} data-testid="tva-prev-year">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <h2 className="text-xl font-bold text-slate-900" data-testid="tva-year">{tvaYear}</h2>
              <Button variant="outline" size="sm" onClick={() => setTvaYear(y => y + 1)} data-testid="tva-next-year">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Annual summary */}
            {tvaMonthly && (
              <Card className="p-5 bg-gradient-to-r from-red-50 to-amber-50 border-red-200" data-testid="tva-annual-summary">
                <h3 className="text-sm font-bold text-red-800 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> Total TVA a recuperer - {tvaYear}
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-slate-500">Total HT</p>
                    <p className="text-xl font-bold text-slate-800">CHF {tvaMonthly.year_total_ht?.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">TVA a recuperer</p>
                    <p className="text-2xl font-bold text-red-700">CHF {tvaMonthly.year_tva_total?.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Total TTC</p>
                    <p className="text-xl font-bold text-slate-800">CHF {tvaMonthly.year_total_ttc?.toFixed(2)}</p>
                  </div>
                </div>
              </Card>
            )}

            {/* Monthly breakdown table */}
            {tvaMonthly && (
              <Card className="overflow-hidden" data-testid="tva-monthly-table">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-800 text-white">
                        <th className="px-4 py-3 text-left font-medium">{t('common.month')}</th>
                        <th className="px-4 py-3 text-right font-medium">Factures</th>
                        <th className="px-4 py-3 text-right font-medium">Total HT</th>
                        <th className="px-4 py-3 text-right font-medium">TVA 8.1%</th>
                        <th className="px-4 py-3 text-right font-medium">TVA 2.6%</th>
                        <th className="px-4 py-3 text-right font-medium">TVA 3.8%</th>
                        <th className="px-4 py-3 text-right font-medium text-red-300">TVA Total</th>
                        <th className="px-4 py-3 text-right font-medium">Total TTC</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tvaMonthly.months?.map((m, idx) => {
                        const tva81 = m.tva_by_rate?.find(t => t.rate === 8.1);
                        const tva26 = m.tva_by_rate?.find(t => t.rate === 2.6);
                        const tva38 = m.tva_by_rate?.find(t => t.rate === 3.8);
                        const hasData = m.count > 0;
                        return (
                          <tr key={m.month} className={`border-b border-slate-100 ${hasData ? 'bg-white' : 'bg-slate-50/50'} ${idx % 2 === 0 ? '' : 'bg-slate-50'}`}
                            data-testid={`tva-month-${m.month}`}>
                            <td className="px-4 py-2.5 font-medium text-slate-700">{m.month_name}</td>
                            <td className="px-4 py-2.5 text-right text-slate-600">{m.count || '-'}</td>
                            <td className="px-4 py-2.5 text-right text-slate-700">{hasData ? `CHF ${m.total_ht?.toFixed(2)}` : '-'}</td>
                            <td className="px-4 py-2.5 text-right text-slate-600">{tva81 ? `CHF ${tva81.amount?.toFixed(2)}` : '-'}</td>
                            <td className="px-4 py-2.5 text-right text-slate-600">{tva26 ? `CHF ${tva26.amount?.toFixed(2)}` : '-'}</td>
                            <td className="px-4 py-2.5 text-right text-slate-600">{tva38 ? `CHF ${tva38.amount?.toFixed(2)}` : '-'}</td>
                            <td className="px-4 py-2.5 text-right font-bold text-red-700">{hasData ? `CHF ${m.tva_total?.toFixed(2)}` : '-'}</td>
                            <td className="px-4 py-2.5 text-right font-medium text-slate-800">{hasData ? `CHF ${m.total_ttc?.toFixed(2)}` : '-'}</td>
                          </tr>
                        );
                      })}
                      {/* Annual total row */}
                      <tr className="bg-slate-800 text-white font-bold" data-testid="tva-annual-total">
                        <td className="px-4 py-3">TOTAL {tvaYear}</td>
                        <td className="px-4 py-3 text-right">{tvaMonthly.months?.reduce((s, m) => s + m.count, 0)}</td>
                        <td className="px-4 py-3 text-right">CHF {tvaMonthly.year_total_ht?.toFixed(2)}</td>
                        <td className="px-4 py-3 text-right">
                          CHF {tvaMonthly.months?.reduce((s, m) => s + (m.tva_by_rate?.find(t => t.rate === 8.1)?.amount || 0), 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          CHF {tvaMonthly.months?.reduce((s, m) => s + (m.tva_by_rate?.find(t => t.rate === 2.6)?.amount || 0), 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          CHF {tvaMonthly.months?.reduce((s, m) => s + (m.tva_by_rate?.find(t => t.rate === 3.8)?.amount || 0), 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-right text-red-300">CHF {tvaMonthly.year_tva_total?.toFixed(2)}</td>
                        <td className="px-4 py-3 text-right">CHF {tvaMonthly.year_total_ttc?.toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* ─── CREATE/EDIT DIALOG ─── */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="supplier-form-dialog">
            <DialogHeader>
              <DialogTitle data-testid="supplier-form-title">
                {editItem ? 'Modifier la facture fournisseur' : fromScan ? 'Verifier et completer la facture' : 'Nouvelle facture fournisseur'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Scan review banner */}
              {fromScan && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800" data-testid="scan-review-banner">
                  <strong>Données extraites par OCR</strong> — Vérifiez les montants et complétez le numéro de facture et le délai de paiement avant d'enregistrer.
                </div>
              )}

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Fournisseur *</label>
                  <Input value={form.supplier_name} onChange={e => setForm(f => ({ ...f, supplier_name: e.target.value }))}
                    placeholder="Nom du fournisseur" data-testid="input-supplier-name" />
                </div>
                <div>
                  <label className={`text-sm font-medium ${fromScan ? 'text-amber-700' : 'text-slate-700'}`}>
                    N de facture {fromScan && <span className="text-xs text-amber-500 ml-1">(a completer)</span>}
                  </label>
                  <Input value={form.invoice_number} onChange={e => setForm(f => ({ ...f, invoice_number: e.target.value }))}
                    placeholder="Ex: FA-2026-001"
                    className={fromScan ? 'border-amber-300 bg-amber-50/50 focus:border-amber-500' : ''}
                    data-testid="input-invoice-number" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Reference</label>
                  <Input value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))}
                    placeholder="N de reference / contrat"
                    data-testid="input-reference" />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Date facture</label>
                  <Input type="date" value={form.invoice_date} onChange={e => {
                    const newDate = e.target.value;
                    setForm(f => {
                      const newDue = f.payment_terms ? applyPaymentTerms(f.payment_terms, newDate) : f.due_date;
                      return { ...f, invoice_date: newDate, due_date: newDue };
                    });
                  }} data-testid="input-invoice-date" />
                </div>
                <div>
                  <label className={`text-sm font-medium ${fromScan ? 'text-amber-700' : 'text-slate-700'}`}>
                    Delai de paiement {fromScan && <span className="text-xs text-amber-500 ml-1">(a completer)</span>}
                  </label>
                  <Select value={form.payment_terms} onValueChange={v => {
                    setForm(f => {
                      const due = applyPaymentTerms(v, f.invoice_date);
                      return { ...f, payment_terms: v, due_date: due || f.due_date };
                    });
                  }}>
                    <SelectTrigger className={fromScan ? 'border-amber-300 bg-amber-50/50' : ''} data-testid="select-payment-terms">
                      <SelectValue placeholder="Choisir..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10 jours</SelectItem>
                      <SelectItem value="20">20 jours</SelectItem>
                      <SelectItem value="30">30 jours</SelectItem>
                      <SelectItem value="45">45 jours</SelectItem>
                      <SelectItem value="60">60 jours</SelectItem>
                      <SelectItem value="90">90 jours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Echeance</label>
                  <Input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
                    data-testid="input-due-date" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Categorie</label>
                  <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                    <SelectTrigger data-testid="select-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Lines */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium text-slate-700">Lignes de detail</label>
                  <Button size="sm" variant="outline" onClick={addLine} data-testid="add-line-btn">
                    <Plus className="w-3 h-3 mr-1" /> Ligne
                  </Button>
                </div>
                <div className="space-y-2">
                  {form.lines.map((line, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-end bg-slate-50 p-2 rounded" data-testid={`supplier-line-${idx}`}>
                      <div className="col-span-5">
                        {idx === 0 && <label className="text-xs text-slate-500">{t('common.description')}</label>}
                        <Input value={line.description} onChange={e => updateLine(idx, 'description', e.target.value)}
                          placeholder="Description" data-testid={`line-desc-${idx}`} />
                      </div>
                      <div className="col-span-2">
                        {idx === 0 && <label className="text-xs text-slate-500">Quantite</label>}
                        <Input type="number" step="0.1" min="0" value={line.quantity}
                          onChange={e => updateLine(idx, 'quantity', parseFloat(e.target.value) || 0)}
                          data-testid={`line-qty-${idx}`} />
                      </div>
                      <div className="col-span-2">
                        {idx === 0 && <label className="text-xs text-slate-500">Prix HT</label>}
                        <Input type="number" step="0.01" min="0" value={line.unit_price}
                          onChange={e => updateLine(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                          data-testid={`line-price-${idx}`} />
                      </div>
                      <div className="col-span-2">
                        {idx === 0 && <label className="text-xs text-slate-500">TVA %</label>}
                        <Select value={String(line.tva_rate)} onValueChange={v => updateLine(idx, 'tva_rate', parseFloat(v))}>
                          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="8.1">8.1%</SelectItem>
                            <SelectItem value="2.6">2.6%</SelectItem>
                            <SelectItem value="3.8">3.8%</SelectItem>
                            <SelectItem value="0">0%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-1 flex justify-center">
                        {form.lines.length > 1 && (
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => removeLine(idx)}>
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex justify-end">
                  <div className="bg-slate-100 rounded-lg p-3 text-right space-y-1 min-w-[220px]">
                    <p className="text-sm text-slate-600">Sous-total HT: <span className="font-medium">CHF {totals.subtotal}</span></p>
                    <p className="text-sm text-red-600 font-medium">TVA: <span className="font-bold">CHF {totals.tva}</span></p>
                    <p className="text-base font-bold text-slate-900 border-t border-slate-300 pt-1">Total TTC: CHF {totals.total}</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Notes</label>
                <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Notes internes..." data-testid="input-notes" />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowForm(false)}>{t('common.cancel')}</Button>
                <Button onClick={handleSave} data-testid="save-supplier-invoice-btn">
                  {editItem ? 'Mettre a jour' : 'Creer'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ─── QR CODE DIALOG ─── */}
        <Dialog open={showQrDialog} onOpenChange={closeQrDialog}>
          <DialogContent className="max-w-sm" data-testid="qr-scan-dialog">
            <DialogHeader>
              <DialogTitle>Scanner avec le telephone</DialogTitle>
            </DialogHeader>
            <div className="text-center space-y-4">
              {scanSession?.qr_code && (
                <img src={scanSession.qr_code} alt="QR Code" className="w-56 h-56 mx-auto border rounded-lg" data-testid="scan-qr-code" />
              )}
              <div className="space-y-2">
                <p className="text-sm text-slate-600">Scannez ce QR code avec votre téléphone</p>
                <p className="text-xs text-slate-400">L'appareil photo s'ouvrira pour photographier la facture</p>
              </div>
              {pollingSession && (
                <div className="flex items-center justify-center gap-2 text-amber-600">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm font-medium">En attente du scan...</span>
                </div>
              )}
              <Button variant="outline" onClick={closeQrDialog} className="w-full">{t('common.close')}</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* ─── DELETE CONFIRM ─── */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent data-testid="delete-confirm-dialog">
            <DialogHeader>
              <DialogTitle>Confirmer la suppression</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-600">
              Supprimer la facture de <strong>{deleteConfirm?.supplier_name}</strong> ({deleteConfirm?.invoice_number}) ?
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>{t('common.cancel')}</Button>
              <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">{t('common.delete')}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default SupplierInvoicesPage;
